"use client"

import React, { useState, useEffect, useRef, useCallback, useMemo } from "react"
import { Outlet, useNavigate, useLocation } from "react-router-dom"
import Sidebar from "../../components/Sidebar"
import { useAuth } from "../../context/AuthProvider"
import { logoutUser, getInquiryByClientId } from "../../services/AgentServices"
import { getTodayComplaintNotifications } from "../../services/AdminService"
import { mapRole } from "../../utils/roleMapper"

const readStoredValue = (key) => sessionStorage.getItem(key) || localStorage.getItem(key) || ""

const normalizeStatus = (status) => String(status || "").toUpperCase().trim()
const normalizeStatusCode = (status) => {
  const value = normalizeStatus(status)
  if (["OPEN", "OPN"].includes(value)) return "OPN"
  if (["IN-PROGRESS", "INPROGRESS", "IN_PROGRESS", "INP"].includes(value)) return "INP"
  if (["RESOLVED", "CLOSED", "CRS", "DONE"].includes(value)) return "CRS"
  if (["NO-RESPONSE", "NO RESPONSE", "NORESPONSE", "CNR"].includes(value)) return "CNR"
  return value
}

const getDateOnly = (value) => {
  if (!value) return ""
  const normalized = String(value).trim()

  if (!normalized || normalized === "0000-00-00" || normalized === "0000-00-00 00:00:00") {
    return ""
  }

  if (/^\d{4}-\d{2}-\d{2}/.test(normalized)) {
    return normalized.slice(0, 10)
  }

  const parsed = new Date(normalized)
  if (Number.isNaN(parsed.getTime())) return ""

  return [
    parsed.getFullYear(),
    String(parsed.getMonth() + 1).padStart(2, "0"),
    String(parsed.getDate()).padStart(2, "0"),
  ].join("-")
}

const AdminLayout = () => {
  const navigate = useNavigate()
  const { logout, user, authReady } = useAuth()
  const location = useLocation()

  const role = useMemo(() => {
    const abbr = user?.abbreviation || readStoredValue("abbreviation")
    if (!abbr) return null
    return mapRole(abbr)
  }, [user])

  const [notifications, setNotifications] = useState({
    showDropdown: false,
    count: 0,
    list: [],
    loading: false,
  })
  const [followupReminder, setFollowupReminder] = useState(null)
  const notificationRef = useRef(null)

  const pageTitle = useMemo(() => {
    const titleMap = {
      "/admin": "Admin Dashboard",
      "/admin/setup": "Setup Atai",
      "/admin/updatedelete": "Manage Options",
      "/admin/add-staff": "Add Staff",
      "/admin/all-staff": "Manage Staff",
      "/admin/UploadExcel": "Provide Product List",
      "/admin/TicketAdmin": "Tickets",
      "/admin/NotificationAdmin": "Notification",
      "/admin/atai-tour": "ATai Tour",
    }
    const path = location.pathname

    if (titleMap[path]) return titleMap[path]

    for (const [route, title] of Object.entries(titleMap)) {
      if (path.includes(route.split("/").pop())) return title
    }

    return "Admin Panel"
  }, [location.pathname])

  const handleLogout = useCallback(async () => {
    const userId = readStoredValue("userId")

    try {
      if (userId) {
        await logoutUser(userId)
      }
    } catch (error) {
      console.error("Logout API error:", error)
    } finally {
      logout()
      navigate("/", { replace: true })
    }
  }, [logout, navigate])

  const handleNotificationClick = useCallback(async () => {
    setNotifications((prev) => ({
      ...prev,
      showDropdown: !prev.showDropdown,
      loading: !prev.showDropdown,
    }))

    if (!notifications.showDropdown) {
      try {
        const response = await getTodayComplaintNotifications()

        if (response?.status && Array.isArray(response.admin_notification)) {
          setNotifications((prev) => ({
            ...prev,
            list: response.admin_notification,
            count: response.admin_notification.length,
            loading: false,
          }))
        } else {
          setNotifications((prev) => ({
            ...prev,
            list: [],
            count: 0,
            loading: false,
          }))
        }
      } catch (error) {
        console.error("Failed to load notifications:", error)
        setNotifications((prev) => ({
          ...prev,
          list: [],
          count: 0,
          loading: false,
        }))
      }
    }
  }, [notifications.showDropdown])

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (notifications.showDropdown && notificationRef.current && !notificationRef.current.contains(event.target)) {
        setNotifications((prev) => ({ ...prev, showDropdown: false }))
      }
    }

    if (notifications.showDropdown) {
      document.addEventListener("mousedown", handleClickOutside)
      return () => document.removeEventListener("mousedown", handleClickOutside)
    }
  }, [notifications.showDropdown])

  useEffect(() => {
    const showTodayInquiryFollowupReminder = async () => {
      const clientId = readStoredValue("clientId")
      if (!clientId) return

      const today = getDateOnly(new Date())
      const reminderKey = `admin-inquiry-followups-shown:${clientId}:${today}`

      if (sessionStorage.getItem(reminderKey) === "true") return

      try {
        const inquiryRes = await getInquiryByClientId(clientId, 1, 1000)
        const allInquiries =
          inquiryRes && (inquiryRes.status || inquiryRes.success) && Array.isArray(inquiryRes.data)
            ? inquiryRes.data
            : []

        const dueTodayInquiries = allInquiries.filter((inq) => {
          const statusCode = normalizeStatusCode(inq?.status || inq?.status_code)
          if (!["OPN", "INP"].includes(statusCode)) return false

          const nextFollowup = inq?.Next_followup ?? inq?.next_followup ?? inq?.nextFollowup ?? ""
          return getDateOnly(nextFollowup) === today
        })

        if (dueTodayInquiries.length === 0) return

        const reminderPreview = dueTodayInquiries
          .slice(0, 3)
          .map((inq) => {
            const inquiryId = inq?.id ?? inq?.inquiry_id ?? inq?.ticket_id ?? "N/A"
            const clientName = inq?.Client_name ?? inq?.client_name ?? inq?.name ?? "Client"
            return `#${inquiryId} ${clientName}`
          })

        sessionStorage.setItem(reminderKey, "true")

        setFollowupReminder({
          title:
            dueTodayInquiries.length === 1
              ? "1 inquiry needs follow-up today."
              : `${dueTodayInquiries.length} inquiries need follow-up today.`,
          subtitle: `Showing Open and In-Progress inquiries scheduled for ${today}.`,
          preview: `${reminderPreview.join(", ")}${dueTodayInquiries.length > reminderPreview.length ? " ..." : ""}`,
        })
      } catch (error) {
        console.error("Failed to load inquiry follow-up reminder:", error)
      }
    }

    showTodayInquiryFollowupReminder()
  }, [])

  const notificationContent = useMemo(() => {
    if (notifications.loading) {
      return (
        <li>
          <span className="dropdown-item d-flex align-items-center justify-content-center p-3">
            <div className="spinner-border spinner-border-sm text-primary me-2" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
            Loading notifications...
          </span>
        </li>
      )
    }

    if (notifications.list.length === 0) {
      return (
        <li>
          <span className="dropdown-item text-muted text-center p-3">
            <i className="fas fa-inbox me-2"></i>
            No new notifications
          </span>
        </li>
      )
    }

    return notifications.list.map((msg, index) => (
      <li key={`notification-${index}`}>
        <span className="dropdown-item d-flex align-items-start p-3 border-bottom">
          <i className="fas fa-bell me-2 text-primary mt-1 flex-shrink-0"></i>
          <span className="text-wrap small">{msg}</span>
        </span>
      </li>
    ))
  }, [notifications.loading, notifications.list])

  return (
    <>
      {!authReady ? (
        <div className="d-flex justify-content-center align-items-center vh-100">
          <div className="spinner-border text-primary"></div>
        </div>
      ) : (
        <div className="d-flex flex-column vh-100">
          {followupReminder && (
            <div className="followup-popup-shell">
              <div className="followup-popup-card">
                <div className="d-flex align-items-start justify-content-between gap-3 mb-2">
                  <div>
                    <p className="mb-1 fw-semibold">{followupReminder.title}</p>
                    <div className="small text-muted">{followupReminder.subtitle}</div>
                  </div>
                  <button
                    type="button"
                    className="btn btn-sm btn-light border followup-popup-close"
                    onClick={() => setFollowupReminder(null)}
                    aria-label="Close notification"
                  >
                    x
                  </button>
                </div>
                <div className="small mb-3">{followupReminder.preview}</div>
                <div className="d-flex justify-content-end gap-2">
                  <button
                    className="btn btn-sm btn-primary"
                    onClick={() => {
                      setFollowupReminder(null)
                      navigate("/admin/components/Inquires", {
                        state: { followupToday: true },
                      })
                    }}
                    type="button"
                  >
                    View inquiries
                  </button>
                  <button
                    className="btn btn-sm btn-secondary"
                    onClick={() => setFollowupReminder(null)}
                    type="button"
                  >
                    Dismiss
                  </button>
                </div>
              </div>
            </div>
          )}

          <nav className="navbar text-white navbar-expand-md px-3 shadow-sm" style={{ backgroundColor: "#1e293b" }}>
            <div className="container-fluid">
              <div className="d-flex flex-column flex-lg-row align-items-start align-items-lg-center">
                <span className="navbar-brand mb-1 mb-lg-0 h1 fw-bold me-lg-4 text-white" title={pageTitle}>
                  {pageTitle}
                </span>
                <div className="d-flex flex-wrap align-items-center gap-3 small opacity-90">
                  <div className="d-flex align-items-center">
                    <i className="fas fa-user me-1"></i>
                    <span className="fw-medium">{user?.name || user?.user_name || "Admin User"}</span>
                  </div>
                </div>
              </div>

              <button
                className="navbar-toggler border-0"
                type="button"
                data-bs-toggle="collapse"
                data-bs-target="#adminNavbar"
                aria-controls="adminNavbar"
                aria-expanded="false"
                aria-label="Toggle navigation"
              >
                <span className="navbar-toggler-icon"></span>
              </button>

              <div className="collapse navbar-collapse justify-content-end" id="adminNavbar">
                <div className="d-flex align-items-center gap-2 mt-2 mt-md-0">
                  <div className="dropdown" ref={notificationRef}>
                    <ul
                      className={`dropdown-menu dropdown-menu-end shadow-lg border-0 ${notifications.showDropdown ? "show" : ""}`}
                      style={{
                        maxHeight: "400px",
                        overflowY: "auto",
                        minWidth: "300px",
                        zIndex: 1055,
                      }}
                    >
                      <li className="dropdown-header d-flex justify-content-between align-items-center bg-light">
                        <span className="fw-bold">Notifications</span>
                        {notifications.count > 0 && <small className="text-muted">{notifications.count} new</small>}
                      </li>
                      <li>
                        <hr className="dropdown-divider m-0" />
                      </li>
                      {notificationContent}
                      {notifications.list.length > 0 && (
                        <>
                          <li>
                            <hr className="dropdown-divider m-0" />
                          </li>
                          <li>
                            <button
                              className="dropdown-item text-center text-primary small py-2"
                              onClick={() => navigate("/admin/NotificationAdmin")}
                            >
                              <i className="fas fa-eye me-1"></i>
                              View all notifications
                            </button>
                          </li>
                        </>
                      )}
                    </ul>
                  </div>

                  <button
                    className="btn btn-outline-light border-0"
                    onClick={handleLogout}
                    title="Logout"
                    style={{ backgroundColor: "#374151" }}
                  >
                    <i className="fas fa-sign-out-alt me-1"></i>
                    <span className="d-none d-sm-inline">Logout</span>
                  </button>
                </div>
              </div>
            </div>
          </nav>

          <div className="d-flex flex-grow-1">
            {role && <Sidebar role={role} ticketCount={notifications.count} />}
            <main className="flex-grow-1 p-2 p-md-3 bg-light overflow-auto">
              <Outlet />
            </main>
          </div>

          <footer className="text-white text-center py-2 mt-auto" style={{ backgroundColor: "#1e293b" }}>
            <small>© {new Date().getFullYear()} Powered By ATai</small>
          </footer>

          <style>{`
            .followup-popup-shell {
              position: fixed;
              top: 76px;
              right: 16px;
              z-index: 1080;
              width: min(360px, calc(100vw - 24px));
            }

            .followup-popup-card {
              border-radius: 20px;
              background: linear-gradient(145deg, #ffffff 0%, #f8fbff 100%);
              border: 1px solid #dbeafe;
              box-shadow: 0 20px 45px rgba(15, 23, 42, 0.16);
              padding: 16px;
            }

            .followup-popup-close {
              width: 32px;
              height: 32px;
              border-radius: 999px;
              padding: 0;
              line-height: 1;
              flex-shrink: 0;
            }

            @media (max-width: 767.98px) {
              .followup-popup-shell {
                top: 72px;
                left: 12px;
                right: 12px;
                width: auto;
              }
            }
          `}</style>
        </div>
      )}
    </>
  )
}

export default React.memo(AdminLayout)
